import { isThermometer } from 'utils/isThermometer';
import create from 'zustand';
import { SerializedMeter } from '@enmon/commons/lib/enmon';
import { useStore, type InstallMeterStore } from '../useStore';
import { InstallationTechnology as InstallationTechnologyEnum } from '../const/installationTechnology';
import { InstallMeterStep } from '../enums/installMeterStep';

const INITIAL_STATE = {
  currentStep: InstallMeterStep.InstallationTechnology,
} as const;

interface SetStepOptions {
  meterDetails: SerializedMeter['detail'];
  installationTechnology?: InstallMeterStore['installationTechnology'];
}

const getNextStep = (currentStep: InstallMeterStep, options: SetStepOptions) => {
  const { meterDetails, installationTechnology } = options;

  switch (currentStep) {
    case InstallMeterStep.InstallationTechnology:
      return InstallMeterStep.SensorScan;

    case InstallMeterStep.SensorScan:
      // We do not check sensor connection for meters with technology other than LoRaWan
      if (installationTechnology !== InstallationTechnologyEnum.LoraWan) return InstallMeterStep.Hardwares;

      return InstallMeterStep.SensorConnection;

    case InstallMeterStep.SensorConnection:
      return InstallMeterStep.Hardwares;

    case InstallMeterStep.Hardwares:
      if (isThermometer(meterDetails)) return InstallMeterStep.Photo;

      return InstallMeterStep.InstallationDetail;

    case InstallMeterStep.InstallationDetail:
      return InstallMeterStep.Photo;

    case InstallMeterStep.Photo:
      return InstallMeterStep.Review;

    default:
      return InstallMeterStep.Review;
  }
};

const getPreviousStep = (currentStep: InstallMeterStep, options: SetStepOptions) => {
  const { meterDetails, installationTechnology } = options;

  switch (currentStep) {
    case InstallMeterStep.Review:
      return InstallMeterStep.Photo;

    case InstallMeterStep.Photo:
      if (isThermometer(meterDetails)) return InstallMeterStep.Hardwares;

      return InstallMeterStep.InstallationDetail;

    case InstallMeterStep.InstallationDetail:
      return InstallMeterStep.Hardwares;

    case InstallMeterStep.Hardwares:
      // We do not check sensor connection for meters with technology other than LoRaWan
      if (installationTechnology !== InstallationTechnologyEnum.LoraWan) return InstallMeterStep.SensorScan;

      return InstallMeterStep.SensorConnection;

    case InstallMeterStep.SensorConnection:
      return InstallMeterStep.SensorScan;

    case InstallMeterStep.SensorScan:
      return InstallMeterStep.InstallationTechnology;

    default:
      return InstallMeterStep.InstallationTechnology;
  }
};

export interface InstallMeterStepsStore {
  currentStep: InstallMeterStep;
  setCurrentStep: (payload: InstallMeterStep) => void;
  /**
   * Sets next install step based on current step and state of installation, defaults to last step (Review)
   */
  setNextStep: () => void;
  /**
   * Sets previous install step based on current step and state of installation, defaults to first step (SensorScan)
   */
  setPreviousStep: () => void;
  // setPreviousStep(): void;
  reset: () => void;
}

export const useInstallMeterStepsStore = create<InstallMeterStepsStore>((set, get) => ({
  ...INITIAL_STATE,
  setCurrentStep: (step: InstallMeterStep) => {
    set(() => ({ currentStep: step }));
  },
  setNextStep() {
    const { currentStep } = get();
    const nextStep = getNextStep(currentStep, {
      meterDetails: useStore.getState().enmonMeter?.detail,
      installationTechnology: useStore.getState().installationTechnology,
    });
    set(() => ({ currentStep: nextStep }));
  },
  setPreviousStep() {
    const { currentStep } = get();
    const previousStep = getPreviousStep(currentStep, {
      meterDetails: useStore.getState().enmonMeter?.detail,
      installationTechnology: useStore.getState().installationTechnology,
    });
    set(() => ({ currentStep: previousStep }));
  },
  reset: () => set(INITIAL_STATE),
}));
